import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { blogPosts, featuredPost } from '../data/blogPosts';

const Blog = () => {
  const { t } = useTranslation();
  const regularPosts = blogPosts.filter(post => post.id !== featuredPost.id);

  return (
    <div className="page-wrapper">
      <SEO 
        title={t('seo.blog.title')}
        description={t('seo.blog.description')}
        keywords={t('seo.blog.keywords')}
        canonical="https://dsotocleaning.com/blog"
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{t('blog.hero_title')}</h1>
          <p className="page-hero-description">{t('blog.hero_desc')}</p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="section-padding bg-light-gray">
        <div className="container">
          <div className="featured-post">
            <div className="featured-post-image">
              <img src={featuredPost.featuredImage} alt={featuredPost.title} />
              <div className="featured-badge">Featured Article</div>
            </div>
            <div className="featured-post-content">
              <div className="blog-meta">
                <span className="blog-category">{featuredPost.category}</span>
                <div className="blog-meta-items">
                  <span className="blog-meta-item">
                    <Calendar size={16} />
                    {new Date(featuredPost.publishDate).toLocaleDateString('en-US', { 
                      year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </span>
                  <span className="blog-meta-item">
                    <Clock size={16} />
                    {featuredPost.readTime}
                  </span>
                </div>
              </div>
              <h2 className="featured-post-title">{featuredPost.title}</h2>
              <p className="featured-post-excerpt">{featuredPost.excerpt}</p>
              <Link to={`/blog/${featuredPost.slug}`}>
                <Button size="lg" className="cta-button">
                  Read Full Article <ArrowRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding">
        <div className="container">
          <h2 className="section-title text-center mb-12">Latest Articles</h2>
          <div className="blog-grid">
            {regularPosts.map((post) => (
              <Card key={post.id} className="blog-card">
                <div className="blog-card-image">
                  <img src={post.featuredImage} alt={post.title} />
                  <span className="blog-card-category">{post.category}</span>
                </div>
                <CardContent className="blog-card-content">
                  <div className="blog-card-meta">
                    <span className="blog-meta-item">
                      <Calendar size={14} />
                      {new Date(post.publishDate).toLocaleDateString('en-US', { 
                        month: 'short', day: 'numeric', year: 'numeric' 
                      })}
                    </span>
                    <span className="blog-meta-item">
                      <Clock size={14} />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="blog-card-title">{post.title}</h3>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`} className="blog-read-more">
                    Read Article <ArrowRight size={16} className="ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <div className="container text-center">
          <h2 className="section-title text-white">Partner with Industry Leaders</h2>
          <p className="section-description text-white centered-text mb-8">
            Discover how D Soto Cleaning delivers operational excellence for corporate fleet operations.
          </p>
          <Link to="/contact">
            <Button size="lg" className="cta-button-white">
              Schedule Consultation <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
